export const buildWelcomeMessage = (job) => {
  const fallback = "Search for candidates by describing what you're looking for — specialty, location, experience level, or any combination."

  if (!job) return fallback

  const bullets = []

  const candidateTypeName = job.candidateType?.name
  const specialties = (job.specialties || []).map(s => s.name).filter(Boolean)

  if (candidateTypeName && specialties.length > 0) {
    bullets.push(`Looking for: ${candidateTypeName} in ${specialties.join(', ')}`)
  } else if (candidateTypeName) {
    bullets.push(`Looking for: ${candidateTypeName}`)
  } else if (specialties.length > 0) {
    bullets.push(`Specialties: ${specialties.join(', ')}`)
  }

  const location = [job.city, job.state].filter(Boolean).join(', ')
  if (location) {
    bullets.push(`Location: ${location}`)
  }

  if (bullets.length === 0) return fallback

  const bulletList = bullets.map(b => `- ${b}`).join('\n')
  return `Here's the job I'm searching candidates for:\n${bulletList}\n\nI'll use this to find and rank candidates. What criteria matter most to you?`
}

export const buildTypingIndicatorMessages = ({ job, candidateListingArguments }) => {
  const searchSpecific = []
  const actionOriented = [
    'Scanning our candidate database...',
    'Evaluating candidate profiles...',
    'Ranking the best matches for your position...',
  ]
  const fun = [
    'Scrubbing in...',
    'Reviewing credentials...',
    'Prepping your results...',
  ]

  const specialty = candidateListingArguments?.specialties?.[0]
    || job?.specialties?.[0]?.name
  const state = candidateListingArguments?.preferredStates?.[0]
    || job?.state

  if (specialty) searchSpecific.push(`Searching for ${specialty} candidates...`)
  if (state) searchSpecific.push(`Looking for candidates in ${state}...`)

  return [...searchSpecific, ...actionOriented, ...fun].slice(0, 8)
}

export const generateExampleQueries = (job) => {
  const DEFAULT_QUERIES = [
    'Board certified cardiologists',
    'Nurses with 5+ years experience',
    'Candidates licensed in Texas',
  ]

  if (!job) return DEFAULT_QUERIES

  const queries = []
  const specialty = job.specialties?.[0]?.name
  const state = job.state
  const candidateType = job.candidateType?.name

  if (specialty && state) {
    queries.push(`${specialty} candidates in ${state}`)
  } else if (specialty) {
    queries.push(`${specialty} candidates`)
  } else if (candidateType && state) {
    queries.push(`${candidateType}s in ${state}`)
  } else {
    queries.push(DEFAULT_QUERIES[0])
  }

  if (specialty) {
    queries.push(`Board certified ${specialty.toLowerCase()} candidates`)
  } else {
    queries.push(DEFAULT_QUERIES[1])
  }

  if (state) {
    queries.push(`Candidates licensed in ${state}`)
  } else {
    queries.push(DEFAULT_QUERIES[2])
  }

  const unique = [...new Set(queries)]
  for (const dq of DEFAULT_QUERIES) {
    if (unique.length >= 3) break
    if (!unique.includes(dq)) unique.push(dq)
  }

  return unique.slice(0, 3)
}

export const generateFollowUpSuggestions = ({ rankedCandidates, candidateListingArguments }) => {
  const suggestions = []
  const hasResults = rankedCandidates?.length > 0
  const specialty = candidateListingArguments?.specialties?.[0]

  if (!hasResults) {
    if (specialty) {
      suggestions.push(`Show all ${specialty} candidates`)
    }
    suggestions.push('Try a broader search')
    suggestions.push('Show actively looking candidates')
    return suggestions.slice(0, 3)
  }

  const states = rankedCandidates
    .flatMap(rc => rc.candidate?.preferredStates || [])
    .filter(Boolean)
  const uniqueStates = [...new Set(states)]

  if (uniqueStates.length > 0) {
    suggestions.push(`Show only candidates in ${uniqueStates[0]}`)
  }

  suggestions.push('Show only board certified')

  if (suggestions.length < 3) {
    suggestions.push('Who has 6+ years experience?')
  }

  if (suggestions.length < 3) {
    suggestions.push('Show actively looking candidates only')
  }

  return suggestions.slice(0, 3)
}
